import * as React from "react";

import { settingsApi, type MeProfile } from "@/api/settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useUIStore } from "@/stores/ui";

export function ChangePassword(): JSX.Element {
  const [profile, setProfile] = React.useState<MeProfile | null>(null);
  const [current, setCurrent] = React.useState("");
  const [next, setNext] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const pushToast = useUIStore((s) => s.pushToast);

  React.useEffect(() => {
    void (async () => {
      try {
        const p = await settingsApi.getProfile();
        setProfile(p);
      } catch (e) {
        pushToast({
          variant: "destructive",
          title: "Không tải được profile",
          description: e instanceof Error ? e.message : String(e),
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [pushToast]);

  // 3 modes: native (has_password), set-initial (odoo only no native), locked
  const isNative = profile?.has_password ?? false;
  const isOdooOnly = profile && !profile.has_password && profile.has_odoo_identity;
  const isLocked = profile && !profile.has_password && !profile.has_odoo_identity;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (next !== confirm) {
      pushToast({ variant: "destructive", title: "Mật khẩu xác nhận không khớp" });
      return;
    }
    if (next.length < 8) {
      pushToast({ variant: "warning", title: "Mật khẩu mới ≥ 8 ký tự" });
      return;
    }
    setBusy(true);
    try {
      if (isOdooOnly) {
        await settingsApi.setInitialPassword({
          odoo_password: current,
          new_password: next,
        });
        pushToast({
          variant: "success",
          title: "Đã tạo native password",
          description: "Lần tới có thể login bằng email + password.",
        });
      } else {
        const result = await settingsApi.changePassword({
          current_password: current,
          new_password: next,
        });
        pushToast({
          variant: "success",
          title: "Đã đổi password",
          description:
            result.mode === "odoo"
              ? "Cập nhật trên Odoo. Login lại với password mới."
              : "Native password updated.",
        });
      }
      setCurrent("");
      setNext("");
      setConfirm("");
      const p = await settingsApi.getProfile();
      setProfile(p);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Đổi thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Đang tải...</div>;
  }

  if (isLocked) {
    return (
      <div className="max-w-md space-y-4">
        <header>
          <h1 className="text-xl font-semibold">Mật khẩu</h1>
        </header>
        <Card>
          <CardContent className="p-5 text-sm space-y-2">
            <p className="text-amber-700">
              Tài khoản chưa có native password và chưa link Odoo identity.
            </p>
            <p className="text-muted-foreground">
              Liên hệ admin reset password qua{" "}
              <code className="text-xs">Admin → Users</code>.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const titleText = isOdooOnly
    ? "Tạo Native Password"
    : "Đổi mật khẩu";
  const helpText = isNative
    ? "Native password — verify với hash bcrypt."
    : isOdooOnly
      ? "Tài khoản hiện chỉ có Odoo identity. Tạo native password để login nhanh hơn (không cần Odoo). Cần Odoo password hiện tại để xác minh."
      : "";
  const currentLabel = isOdooOnly ? "Mật khẩu Odoo hiện tại" : "Mật khẩu hiện tại";

  return (
    <div className="max-w-md space-y-6">
      <header>
        <h1 className="text-xl font-semibold">{titleText}</h1>
        {helpText ? (
          <p className="text-sm text-muted-foreground">{helpText}</p>
        ) : null}
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isOdooOnly ? "Native Password (mới)" : "Mật khẩu mới"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {currentLabel}
              </label>
              <Input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Mật khẩu mới
              </label>
              <Input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tối thiểu 8 ký tự. Khuyến nghị có chữ hoa, số, ký tự đặc biệt.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Xác nhận mật khẩu mới
              </label>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={busy}>
                {busy
                  ? "Đang xử lý..."
                  : isOdooOnly
                    ? "Tạo native password"
                    : "Đổi mật khẩu"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {profile?.has_odoo_identity && profile.has_password ? (
        <p className="text-xs text-muted-foreground">
          Note: tài khoản đã có cả native password lẫn Odoo identity. Đổi password
          chỉ ảnh hưởng native — Odoo password riêng (đổi qua Odoo trực tiếp).
        </p>
      ) : null}
    </div>
  );
}
